import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useRazorpay } from 'react-razorpay';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Check, Building2, Monitor, Coffee, Presentation, Armchair } from 'lucide-react';
import { VenueSelector } from '../components/booking/VenueSelector';
import { DateTimeSelector } from '../components/booking/DateTimeSelector';
import { BookingSummary } from '../components/booking/BookingSummary';
import { PaymentStep } from '../components/booking/PaymentStep';
import { OfferBanner } from '../components/OfferBanner';
import { SaleOfferBanner } from '../components/SaleOfferBanner';
import {
  usePromotion, otherDiscountsSuppressed, dayPassFreeQuantity,
  meetingRoomRewardPreview, meetingRoomRewardFromBackend,
} from '../lib/promotions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, api } = useAuth();
  const { Razorpay } = useRazorpay();
  const promotion = usePromotion();
  const couponsPaused = !!promotion?.other_discounts_suppressed;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1: Venue & Space
  const [venues, setVenues] = useState([]);
  const [allBookables, setAllBookables] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedBookable, setSelectedBookable] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [bookableType, setBookableType] = useState('all');
  const bookablesRef = useRef(null);

  // Step 2: Date & Time
  const [selectedDate, setSelectedDate] = useState(null);
  // Day Pass only: every date picked (sorted). Two or more = one purchase
  // covering all of them, paid together with one bill (POST /bookings/day-pass-multi).
  const [selectedDates, setSelectedDates] = useState([]);
  const [multiDateUnavailable, setMultiDateUnavailable] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [availability, setAvailability] = useState(null);

  // Step 3: Details
  const [attendees, setAttendees] = useState(1);
  const [notes, setNotes] = useState('');
  const [wallet, setWallet] = useState(null);
  // Checkout payment choice — 'money' (card / UPI QR via Razorpay), 'credits'
  // or 'day_pass'. Never applied silently: the customer picks it at checkout.
  const [paymentMethod, setPaymentMethod] = useState('money');
  const paymentMethodTouched = useRef(false);
  const [creditQuote, setCreditQuote] = useState(null);
  const [creditQuoteLoading, setCreditQuoteLoading] = useState(false);
  const [dayPassBalance, setDayPassBalance] = useState(null);
  const [isInternal, setIsInternal] = useState(false);
  const [bookedFor, setBookedFor] = useState('');
  // Day-pass bulk booking (public flow) — mirrors CM/Sales walk-in modal.
  const [passQuantity, setPassQuantity] = useState(1);
  const [passDays, setPassDays] = useState(1);
  const [addOns, setAddOns] = useState({
    parking: { enabled: false, qty: 1 },
    sandwich: { enabled: false, qty: 1 },
    cookies: { enabled: false, qty: 1 },
    stationery: { enabled: false, qty: 1 }
  });

  // Step 4: Payment
  const [bookingResult, setBookingResult] = useState(null);
  const [bookingCreatedAt, setBookingCreatedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  // Mirrors backend/payment_timeout_scheduler.py — self-service bookings must
  // be paid within this window or they're auto-cancelled and the slot released.
  const PAYMENT_WINDOW_MS = 12 * 60 * 1000;
  const paymentDeadline = bookingCreatedAt ? bookingCreatedAt + PAYMENT_WINDOW_MS : null;
  const paymentMsLeft = paymentDeadline ? Math.max(0, paymentDeadline - nowTick) : 0;
  const paymentExpired = !!(bookingResult?.requires_payment && paymentDeadline && paymentMsLeft <= 0);

  // Tick every second while a payment is pending, so the countdown stays live
  // and the Pay Now button hides itself the instant the window lapses — the
  // backend cancels the booking at this same deadline, so leaving the "Pay
  // Now" button live past it would let a customer pay into a dead booking.
  useEffect(() => {
    if (!bookingResult?.requires_payment || bookingResult?.status !== 'pending') return;
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [bookingResult?.requires_payment, bookingResult?.status]);

  // Coupon (self-service external booking only)
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount_percentage, discount_amount, final_amount, message}
  const [couponBusy, setCouponBusy] = useState(false);

  // Regions
  const regions = useMemo(() => {
    const unique = [...new Set(venues.filter(v => v.region).map(v => v.region))];
    return [{ id: 'all', label: 'All Regions' }, ...unique.map(r => ({ id: r, label: r }))];
  }, [venues]);

  const productTypes = [
    { id: 'all', label: 'All', icon: Building2 },
    { id: 'meeting_room', label: 'Meeting Room', icon: Monitor },
    { id: 'conference_room', label: 'Conference Room', icon: Presentation },
    { id: 'day_pass', label: 'Day Pass', icon: Coffee },
    { id: 'dedicated_desk', label: 'Dedicated Desk', icon: Armchair },
  ];

  const bookableTypes = [
    { id: 'all', label: 'All Types', icon: Building2 },
    { id: 'meeting_room', label: 'Meeting Rooms', icon: Monitor },
    { id: 'conference_room', label: 'Conference Rooms', icon: Presentation },
    { id: 'day_pass', label: 'Day Pass', icon: Coffee },
    { id: 'dedicated_desk', label: 'Dedicated Desk', icon: Armchair },
  ];

  // Filtered venues
  const filteredVenues = useMemo(() => {
    let result = venues;
    if (regionFilter !== 'all') result = result.filter(v => v.region === regionFilter);
    if (productFilter !== 'all') {
      const venueIdsWithProduct = new Set(allBookables.filter(b => b.bookable_type === productFilter).map(b => b.venue_id));
      result = result.filter(v => venueIdsWithProduct.has(v.id));
    }
    return result;
  }, [venues, regionFilter, productFilter, allBookables]);

  // Filtered bookables for selected venue
  const bookables = useMemo(() => {
    let result = allBookables.filter(b => b.venue_id === selectedVenue);
    if (bookableType !== 'all') result = result.filter(b => b.bookable_type === bookableType);
    return result;
  }, [allBookables, selectedVenue, bookableType]);

  const selectedVenueData = venues.find(v => v.id === selectedVenue);
  const selectedBookableData = allBookables.find(b => b.id === selectedBookable);
  const isDailyBookable = selectedBookableData?.booking_mode === 'daily';
  const dateCount = isDailyBookable ? Math.max(1, selectedDates.length) : 1;
  const isMultiDate = isDailyBookable && selectedDates.length > 1;

  const selectDayPassDates = (dates) => {
    const sorted = [...(dates || [])].sort((a, b) => a - b);
    setSelectedDates(sorted);
    setSelectedDate(sorted[0] || null);
  };

  // Time generation with 15-minute intervals
  const generateTimeOptions = () => {
    const times = [];
    const venue = selectedVenueData;
    const startH = parseInt(venue?.opening_time?.split(':')[0] || '6');
    const endH = parseInt(venue?.closing_time?.split(':')[0] || '22');
    for (let h = startH; h <= endH; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === endH && m > 0) break;
        times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();
  const isToday = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const getFilteredStartTimes = () => {
    if (!isToday) return timeOptions.slice(0, -4); // leave room for at least 1hr end time
    const now = new Date();
    const cutoffMinutes = now.getHours() * 60 + now.getMinutes() + 15;
    return timeOptions.filter(t => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m >= cutoffMinutes;
    }).slice(0, -4);
  };

  const getAvailableEndTimes = () => {
    if (!startTime) return [];
    const [sh, sm] = startTime.split(':').map(Number);
    const minEndMins = sh * 60 + sm + 60; // at least 1 hour after start
    return timeOptions.filter(t => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m >= minEndMins;
    });
  };

  const isTimeSlotAvailable = (start, end) => {
    if (!availability?.slots) return false;
    return availability.slots.filter(s => s.start >= start && s.start < end).every(s => s.available);
  };

  // Calculate total with add-ons (quantity-based, mode-aware)
  const calculateTotal = () => {
    if (!startTime || !endTime || !availability?.bookable) return { baseAmount: 0, gstAmount: 0, addOnsTotal: 0, total: 0, hours: 0, addOnBreakdown: [], baseLabel: '', autoDiscountLabel: '', autoDiscountAmount: 0 };
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const hours = (eh * 60 + em - sh * 60 - sm) / 60;
    const bookable = availability.bookable;
    const mode = bookable.booking_mode || 'hourly';
    const qty = mode === 'daily' ? Math.max(1, parseInt(passQuantity) || 1) : 1;
    const days = mode === 'daily' ? Math.max(1, parseInt(passDays) || 1) : 1;
    // Several dates in one purchase: each date is priced like a single-date
    // booking, and the bulk discount counts the passes across all of them.
    const dates = mode === 'daily' ? dateCount : 1;
    const isBulk = mode === 'daily' && qty * dates >= 5 && !otherDiscountsSuppressed();
    let baseAmount;
    let baseLabel;
    if (mode === 'daily') {
      // `days` is validity window only — does NOT multiply the base price.
      baseAmount = bookable.hourly_rate * qty * dates;
      const suffix = dates > 1
        ? ` (${qty} pass${qty !== 1 ? 'es' : ''} × ${dates} dates)`
        : qty > 1
          ? ` (${qty} passes)`
          : '';
      baseLabel = `Day Pass (₹${bookable.hourly_rate?.toLocaleString('en-IN')}/day)${suffix}`;
    } else if (mode === 'monthly') {
      baseAmount = bookable.hourly_rate;
      baseLabel = `Dedicated Desk (₹${bookable.hourly_rate?.toLocaleString('en-IN')}/month)`;
    } else {
      baseAmount = hours * bookable.hourly_rate;
      baseLabel = `Base (${hours.toFixed(2)} hrs × ₹${bookable.hourly_rate})`;
    }
    // Auto-discount for half-day/full-day (hourly bookings only) OR 10% bulk day-pass discount.
    let autoDiscountLabel = '';
    let autoDiscountAmount = 0;
    if (otherDiscountsSuppressed()) {
      // Sale running: its offer is the only discount (mirrors the backend).
    } else if (mode === 'hourly') {
      if (hours >= 9) { autoDiscountAmount = baseAmount * 0.20; autoDiscountLabel = 'Full Day Discount (20%)'; }
      else if (hours >= 4.5) { autoDiscountAmount = baseAmount * 0.10; autoDiscountLabel = 'Half Day Discount (10%)'; }
    } else if (isBulk) {
      autoDiscountAmount = baseAmount * 0.10;
      autoDiscountLabel = 'Bulk Day Pass Discount (10%)';
    }
    baseAmount = baseAmount - autoDiscountAmount;

    // Parking add-ons are per-pass; validity window (`days`) does NOT multiply them.
    const addonHours = Math.ceil(hours);

    const addOnBreakdown = [];
    let addOnsTotal = 0;
    if (addOns.parking.enabled) { const c = 50 * addonHours * addOns.parking.qty; addOnsTotal += c; addOnBreakdown.push({ name: '4-Wheeler Parking', detail: `₹50 × ${addonHours} hr × ${addOns.parking.qty}`, amount: c }); }
    if (addOns.sandwich.enabled) { const c = 100 * addOns.sandwich.qty; addOnsTotal += c; addOnBreakdown.push({ name: 'Sandwich', detail: `₹100 × ${addOns.sandwich.qty}`, amount: c }); }
    if (addOns.cookies.enabled) { const c = 50 * addOns.cookies.qty; addOnsTotal += c; addOnBreakdown.push({ name: 'Cookies', detail: `₹50 × ${addOns.cookies.qty}`, amount: c }); }
    if (addOns.stationery.enabled) { const c = 50 * addOns.stationery.qty; addOnsTotal += c; addOnBreakdown.push({ name: 'Stationery (Pen + Notepad)', detail: `₹50 × ${addOns.stationery.qty}`, amount: c }); }
    if (dates > 1) {
      // Add-ons are per date, like a single-date booking's.
      addOnBreakdown.forEach((a) => { a.amount *= dates; a.detail += ` × ${dates} dates`; });
      addOnsTotal *= dates;
    }
    const taxableAmount = baseAmount + addOnsTotal;
    const gstRate = bookable.gst_applicable ? (bookable.gst_rate || 18) : 0;
    const gstAmount = taxableAmount * (gstRate / 100);
    return { baseAmount: baseAmount + autoDiscountAmount, addOnsTotal, addOnBreakdown, gstRate, gstAmount, total: taxableAmount + gstAmount, hours, baseLabel, autoDiscountLabel, autoDiscountAmount, passQuantity: qty, passDays: days, isBulk, dateCount: dates, totalPasses: qty * dates };
  };

  // Data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venuesRes, bookablesRes] = await Promise.all([
          axios.get(`${API}/venues`),
          axios.get(`${API}/bookables`)
        ]);
        setVenues(venuesRes.data);
        setAllBookables(bookablesRes.data);
        const preVenue = searchParams.get('venue') || searchParams.get('venue_id');
        const preSpace = searchParams.get('space') || searchParams.get('bookable_id');
        if (preVenue) setSelectedVenue(preVenue);
        if (preSpace) {
          setSelectedBookable(preSpace);
          // Deep-linked to a specific room (e.g. "Book Now" on a space card) — skip venue/space selection.
          setStep(2);
        }
        // Deep-linked with a date the customer already picked on the website
        // (e.g. the clayworks.space Meeting Rooms page) — preselect it if it's
        // still bookable (not past, not a Sunday). Holidays are re-checked by
        // the availability call as usual.
        const preDate = searchParams.get('date');
        if (preDate && /^\d{4}-\d{2}-\d{2}$/.test(preDate)) {
          const [y, m, d] = preDate.split('-').map(Number);
          const picked = new Date(y, m - 1, d);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (!isNaN(picked.getTime()) && picked >= today && picked.getDay() !== 0) {
            setSelectedDate(picked);
            setSelectedDates([picked]);
          }
        }
        // Deep-linked from a marketing page (e.g. "Buy Bulk Day Passes") — preselect the
        // product type and, for day passes, a starting quantity.
        const preType = searchParams.get('type');
        if (preType && ['meeting_room', 'conference_room', 'day_pass', 'dedicated_desk'].includes(preType)) {
          setProductFilter(preType);
          setBookableType(preType);
        }
        const preQty = searchParams.get('qty');
        if (preQty) setPassQuantity(Math.max(1, parseInt(preQty) || 1));
        // Pre-fill (not auto-submit) a coupon code from a marketing link — the
        // customer still presses "Apply" themselves once they reach that step.
        const preCoupon = searchParams.get('coupon') || searchParams.get('coupon_code');
        if (preCoupon && !otherDiscountsSuppressed()) setCouponCode(preCoupon.trim().toUpperCase());
      } catch (err) {
        toast.error('Failed to load data');
      }
    };
    fetchData();
  }, [searchParams]);

  // Fetch availability when date changes
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedBookable || !selectedDate) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/availability/${selectedBookable}?date=${format(selectedDate, 'yyyy-MM-dd')}`);
        setAvailability(res.data);
        // Auto-set times for day pass (9:00 AM - 8:00 PM) and dedicated desk
        // (9:00 AM - 6:00 PM, matching the CM/Sales walk-in flow) bookings —
        // neither shows a time picker, so these are placeholders the backend
        // will use for the actual reservation window.
        const bookable = allBookables.find(b => b.id === selectedBookable);
        if (bookable?.booking_mode === 'daily') {
          setStartTime('09:00');
          setEndTime('20:00');
        } else if (bookable?.booking_mode === 'monthly') {
          setStartTime('09:00');
          setEndTime('18:00');
        }
      } catch (err) {
        toast.error('Failed to load availability');
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
    const bookable = allBookables.find(b => b.id === selectedBookable);
    if (bookable?.booking_mode !== 'daily' && bookable?.booking_mode !== 'monthly') {
      setStartTime('');
      setEndTime('');
    }
  }, [selectedBookable, selectedDate, allBookables]);

  // Several Day Pass dates: check the other dates are open too (the first is
  // covered by `availability` above). The backend re-checks seats on booking.
  const extraDateKeys = isMultiDate ? selectedDates.slice(1).map(d => format(d, 'yyyy-MM-dd')).join(',') : '';
  useEffect(() => {
    if (!extraDateKeys || !selectedBookable) { setMultiDateUnavailable([]); return; }
    let cancelled = false;
    Promise.all(extraDateKeys.split(',').map(d => axios.get(`${API}/availability/${selectedBookable}?date=${d}`)
      .then(res => ((res.data?.slots || []).filter(s => s.start >= '09:00' && s.start < '20:00').every(s => s.available) ? null : d))
      .catch(() => null)))
      .then((rows) => { if (!cancelled) setMultiDateUnavailable(rows.filter(Boolean)); });
    return () => { cancelled = true; };
  }, [extraDateKeys, selectedBookable]);

  // Fetch wallet as soon as user is authenticated — earlier than step 3 to
  // avoid race conditions where Confirm is clicked before the wallet loads.
  useEffect(() => {
    const fetchWallet = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await api.get('/credits/wallet');
        setWallet(res.data);
      } catch (e) {
        // Wallet not available — "Pay with Credits" shows as unavailable
      }
    };
    fetchWallet();

    const fetchDayPassBalance = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await api.get('/day-pass-packages/my-balance');
        setDayPassBalance(res.data);
      } catch (e) {
        // Not available — the Day Pass balance option just isn't offered
      }
    };
    fetchDayPassBalance();
  }, [isAuthenticated, api]);

  // A Day Pass package can cover a multi-pass booking too (e.g. 10 people in one
  // go), as long as the client's balance is enough to cover the FULL requested
  // quantity — the backend won't split a booking between package + paid, so the
  // preview here mirrors that all-or-nothing rule.
  const dayPassRequestedQty = Math.max(1, parseInt(passQuantity) || 1);
  // Several dates are paid by card only (one order, one bill).
  const dayPassApplicable = selectedBookableData?.bookable_type === 'day_pass' && !isMultiDate
    && (dayPassBalance?.total_remaining || 0) >= dayPassRequestedQty;

  // Default to the Day Pass balance when it covers the booking (it's already
  // paid for), unless the customer has picked a method themselves.
  useEffect(() => {
    if (paymentMethodTouched.current) return;
    setPaymentMethod(dayPassApplicable ? 'day_pass' : 'money');
  }, [dayPassApplicable]);

  const choosePaymentMethod = (method) => {
    paymentMethodTouched.current = true;
    setPaymentMethod(method);
  };

  // Credits this booking would cost and whether the wallet covers it — the
  // backend's own calculation, so the option is only offered when it'll work.
  useEffect(() => {
    if (step !== 3 || !isAuthenticated || isInternal || !selectedBookable || !startTime || !endTime) return;
    let cancelled = false;
    setCreditQuoteLoading(true);
    api.post('/bookings/credit-quote', {
      bookable_id: selectedBookable,
      start_time: startTime,
      end_time: endTime,
      pass_quantity: dayPassRequestedQty,
    })
      .then((res) => { if (!cancelled) setCreditQuote(res.data); })
      .catch(() => { if (!cancelled) setCreditQuote(null); })
      .finally(() => { if (!cancelled) setCreditQuoteLoading(false); });
    return () => { cancelled = true; };
  }, [step, isAuthenticated, isInternal, selectedBookable, startTime, endTime, dayPassRequestedQty, api]);

  // What will actually be used: a method that no longer fits (e.g. quantity
  // raised past the Day Pass balance) falls back to money.
  const effectivePaymentMethod =
    isMultiDate ? 'money'
    : paymentMethod === 'day_pass' && !dayPassApplicable ? 'money'
      : paymentMethod === 'credits' && !creditQuote?.sufficient ? 'money'
        : paymentMethod;
  const useCredits = !isInternal && effectivePaymentMethod === 'credits';
  // Meeting Room 2+2 at checkout: the backend's figures (credit quote) when
  // logged in, otherwise an on-screen estimate.
  const checkoutTotals = step === 3 ? calculateTotal() : null;
  const meetingRoomReward = !isInternal && checkoutTotals
    ? (meetingRoomRewardFromBackend(creditQuote?.promo_reward, checkoutTotals.total)
      || meetingRoomRewardPreview({
        bookableType: selectedBookableData?.bookable_type,
        hours: checkoutTotals.hours,
        paidAmount: checkoutTotals.total,
        paidCredits: creditQuote?.credits_required || 0,
      }))
    : null;
  const useDayPass = !isInternal && effectivePaymentMethod === 'day_pass';

  // Sale reward preview (e.g. Day Pass buy X, get X free). Earned on a card purchase by a
  // logged-in, non-guest client — not when paying with passes or credits.
  // The backend decides; this only previews it. Counts every pass across all
  // the dates picked (1 pass on each of 3 dates = 3 paid passes).
  const promoFreePasses = selectedBookableData?.bookable_type === 'day_pass' && !isInternal
    ? dayPassFreeQuantity(dayPassRequestedQty * dateCount) : 0;
  const promoReward = promoFreePasses > 0 ? {
    freePasses: promoFreePasses,
    multiDate: isMultiDate,
    saleName: promotion?.name,
    validUntil: promotion?.reward_valid_until,
    eligible: isAuthenticated && !user?.is_guest && user?.role === 'client',
  } : null;

  const handleApplyCoupon = async () => {
    if (!couponCode || !couponCode.trim()) { toast.error('Please enter a coupon code'); return; }
    if (isInternal) { toast.error('Coupons cannot be applied on internal bookings'); return; }
    const totals = calculateTotal();
    if (!totals || !totals.total) { toast.error('Pick a space and time first'); return; }
    const taxable = (totals.baseAmount - (totals.autoDiscountAmount || 0)) + (totals.addOnsTotal || 0);
    setCouponBusy(true);
    try {
      const res = await api.post('/coupons/validate', {
        code: couponCode.trim(),
        bookable_id: selectedBookable,
        venue_id: selectedVenue,
        amount: taxable,
      });
      setAppliedCoupon(res.data);
      toast.success(res.data.message);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || 'Coupon could not be applied');
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const addOnQuantities = () => ({
    parking: addOns.parking.enabled ? addOns.parking.qty : 0,
    sandwich: addOns.sandwich.enabled ? addOns.sandwich.qty : 0,
    cookies: addOns.cookies.enabled ? addOns.cookies.qty : 0,
    stationery: addOns.stationery.enabled ? addOns.stationery.qty : 0
  });

  const handleCreateBooking = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (isMultiDate && isInternal) { toast.error('Internal bookings are made one date at a time. Go back and pick a single date.'); return; }
    setLoading(true);
    try {
      const response = isMultiDate && !isInternal ? await api.post('/bookings/day-pass-multi', {
        bookable_id: selectedBookable,
        venue_id: selectedVenue,
        dates: selectedDates.map(d => format(d, 'yyyy-MM-dd')),
        pass_quantity: dayPassRequestedQty,
        attendees,
        notes,
        add_ons: addOnQuantities(),
      }) : await api.post('/bookings', {
        bookable_id: selectedBookable,
        venue_id: selectedVenue,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime,
        attendees,
        notes,
        // Explicit either way, so the backend never applies credits or Day
        // Pass balance the customer didn't choose.
        use_credits: useCredits,
        use_day_pass: useDayPass,
        is_internal: isInternal,
        booked_for: isInternal ? bookedFor : undefined,
        coupon_code: appliedCoupon && !isInternal && !couponsPaused ? appliedCoupon.code : undefined,
        pass_quantity: selectedBookableData?.booking_mode === 'daily' ? Math.max(1, parseInt(passQuantity) || 1) : 1,
        pass_days: selectedBookableData?.booking_mode === 'daily' ? Math.max(1, parseInt(passDays) || 1) : 1,
        add_ons: addOnQuantities()
      });
      setBookingResult(response.data);
      setBookingCreatedAt(Date.now());
      setStep(4);
      toast.success('Booking created!');
      if (dayPassApplicable) {
        api.get('/day-pass-packages/my-balance').then((res) => setDayPassBalance(res.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!bookingResult) return;
    if (paymentExpired) {
      toast.error('This booking has expired because payment was not completed in time. Please create a new booking.');
      return;
    }
    try {
      const orderRes = await api.post('/payments/create-order', {
        booking_id: bookingResult.id,
        amount: bookingResult.total_amount,
        currency: 'INR'
      });
      const { order_id, amount, currency, key_id } = orderRes.data;
      // Cap the checkout's own countdown to whatever's left of the booking's
      // payment window, so Razorpay auto-closes the modal at the same moment
      // the backend sweep would cancel the booking — the customer can no
      // longer complete a payment the backend is about to (or already did)
      // discard as expired.
      const secondsLeft = paymentDeadline ? Math.max(1, Math.floor((paymentDeadline - Date.now()) / 1000)) : undefined;
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Clayworks',
        description: `Booking: ${selectedBookableData?.name}`,
        order_id: order_id,
        timeout: secondsLeft,
        handler: async function (response) {
          try {
            await api.post('/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: bookingResult.id
            });
            setBookingResult(prev => ({ ...prev, status: 'confirmed', requires_payment: false }));
            toast.success('Payment successful! Booking confirmed.');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        },
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#7c3aed' }
      };
      const rzpInstance = new Razorpay(options);
      rzpInstance.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-6xl mx-auto px-6">
        {/* Clickable Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3, 4].map((s, idx) => (
            <React.Fragment key={s}>
              <button
                onClick={() => { if (s < step) setStep(s); }}
                disabled={s >= step}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                  step >= s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                } ${s < step ? 'cursor-pointer hover:ring-2 hover:ring-primary/40 hover:scale-110' : s === step ? '' : 'cursor-default'}`}
                data-testid={`step-indicator-${s}`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </button>
              {idx < 3 && <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-primary' : 'bg-secondary'}`} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && <SaleOfferBanner focus={productFilter} />}
        {step === 1 && selectedVenueData && <OfferBanner venue={selectedVenueData} />}

        {step === 1 && (
          <VenueSelector
            regions={regions} regionFilter={regionFilter} setRegionFilter={setRegionFilter}
            productTypes={productTypes} productFilter={productFilter} setProductFilter={setProductFilter}
            filteredVenues={filteredVenues} selectedVenue={selectedVenue} setSelectedVenue={setSelectedVenue}
            setSelectedBookable={setSelectedBookable} bookablesRef={bookablesRef}
            bookableTypes={bookableTypes} bookableType={bookableType} setBookableType={setBookableType}
            bookables={bookables} selectedBookable={selectedBookable}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <DateTimeSelector
            selectedBookableData={selectedBookableData} selectedVenueData={selectedVenueData}
            selectedDate={selectedDate} setSelectedDate={(d) => selectDayPassDates(d ? [d] : [])}
            selectedDates={selectedDates} onSelectDayPassDates={selectDayPassDates}
            multiDateUnavailable={multiDateUnavailable}
            startTime={startTime} setStartTime={setStartTime} endTime={endTime} setEndTime={setEndTime}
            loading={loading} availability={availability}
            getFilteredStartTimes={getFilteredStartTimes} getAvailableEndTimes={getAvailableEndTimes}
            isTimeSlotAvailable={isTimeSlotAvailable}
            addOns={addOns} setAddOns={setAddOns} calculateTotal={calculateTotal}
            passQuantity={passQuantity} setPassQuantity={setPassQuantity}
            passDays={passDays} setPassDays={setPassDays}
            onBack={() => setStep(1)} onNext={() => setStep(3)}
            wallet={wallet} user={user}
            dayPassBalance={dayPassBalance} dayPassApplicable={dayPassApplicable}
            promoReward={promoReward} bulkDiscountOn={!couponsPaused}
          />
        )}

        {step === 3 && (
          <BookingSummary
            selectedVenueData={selectedVenueData} selectedBookableData={selectedBookableData}
            selectedDate={selectedDate} selectedDates={isMultiDate ? selectedDates : null}
            startTime={startTime} endTime={endTime}
            attendees={attendees} setAttendees={setAttendees} notes={notes} setNotes={setNotes}
            isInternal={isInternal} setIsInternal={setIsInternal} bookedFor={bookedFor} setBookedFor={setBookedFor}
            useCredits={useCredits} wallet={wallet}
            useDayPass={useDayPass} dayPassBalance={dayPassBalance} dayPassApplicable={dayPassApplicable}
            paymentMethod={effectivePaymentMethod} onPaymentMethodChange={choosePaymentMethod}
            creditQuote={creditQuote} creditQuoteLoading={creditQuoteLoading}
            calculateTotal={calculateTotal} bookingResult={bookingResult}
            user={user} isAuthenticated={isAuthenticated} loading={loading}
            couponCode={couponCode} setCouponCode={couponsPaused || isMultiDate ? undefined : setCouponCode}
            promoReward={promoReward} meetingRoomReward={meetingRoomReward}
            appliedCoupon={appliedCoupon} couponBusy={couponBusy}
            onApplyCoupon={handleApplyCoupon} onRemoveCoupon={handleRemoveCoupon}
            onBack={() => setStep(2)} onConfirm={handleCreateBooking}
          />
        )}

        {step === 4 && bookingResult && (
          <PaymentStep
            bookingResult={bookingResult}
            handlePayment={handlePayment}
            paymentExpired={paymentExpired}
            paymentMsLeft={paymentMsLeft}
            onStartNewBooking={() => {
              setBookingResult(null);
              setBookingCreatedAt(null);
              setStep(1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default BookingPage;
